/**
 * The field-tint debug switch, stored per browser and applied as `data-field-audit` on <html>.
 *
 * An attribute plus a CSS block, deliberately: no component anywhere needs to know the switch
 * exists, the tints cost nothing when off, and turning the feature off for good is deleting one
 * rule from globals.css rather than unpicking props from a hundred call sites.
 *
 * Persisted because it is a working mode, not a per-page choice — a reload while debugging should
 * not silently drop you back to plain fields and let a mis-filed control hide again.
 */
import { useCallback, useEffect, useState } from 'react';

const KEY = 'agavi.fieldAudit';

function apply(on: boolean): void {
  document.documentElement.setAttribute('data-field-audit', on ? 'on' : 'off');
}

export function useFieldAudit(): { fieldAudit: boolean; setFieldAudit: (on: boolean) => void } {
  const [fieldAudit, setState] = useState<boolean>(() => localStorage.getItem(KEY) === 'on');

  useEffect(() => { apply(fieldAudit); }, [fieldAudit]);

  const setFieldAudit = useCallback((on: boolean) => {
    localStorage.setItem(KEY, on ? 'on' : 'off');
    setState(on);
  }, []);

  return { fieldAudit, setFieldAudit };
}
